import React, { useState, useEffect } from 'react';
import { fetchApi } from '../../utils/api';

// Function to transform database data into component format for rotating investors
const transformRotatingVentureFundsData = (rotatingVentureFundsData) => {
  if (!rotatingVentureFundsData) return { companyData: {}, labelArrays: { first: [], second: [], third: [] } };

  // Convert to array and sort by rotating_investor_rank
  const sortedFunds = Object.entries(rotatingVentureFundsData)
    .map(([key, value]) => ({ key, ...value }))
    .sort((a, b) => a.rotating_investor_rank - b.rotating_investor_rank);

  // Create companyData object
  const companyData = {};
  const allKeys = [];

  // Add sorted funds
  sortedFunds.forEach(fund => {
    companyData[fund.key] = {
      text: fund.text,
      logo: fund.logo
    };
    allKeys.push(fund.key);
  });

  // Split into three rows in rank order; earlier rows get the extra fund when
  // the count doesn't divide evenly
  const rowSize = Math.ceil(allKeys.length / 3);

  return {
    companyData,
    labelArrays: {
      first: allKeys.slice(0, rowSize),
      second: allKeys.slice(rowSize, rowSize * 2),
      third: allKeys.slice(rowSize * 2)
    }
  };
};
  
const InvestorScroll = ({ speed = '30s', reverse = false, variant = 'first', companyData = {}, labelArrays = {} }) => {
  const labels = labelArrays[variant] || [];
  const totalLabels = labels.length;

  return (
    <div className="logo-loop">
      <div 
        className="logo-track"
        style={{
          '--total-items': totalLabels,
          '--animation-duration': speed,
          'flexDirection': reverse ? 'row-reverse' : 'row'
        }}
      >
        {[...labels, ...labels].map((label, index) => (
          <span 
            key={`label-${index}`}
            className="label"
          >
            {companyData[label]?.logo && (
              <img 
                src={companyData[label].logo} 
                alt={`${label} logo`}
                className="school-logo"
              />
            )}
            {companyData[label]?.text || label}
          </span>
        ))}
      </div>
    </div>
  );
}; 

function InvestorsRotating() {
  const [rotatingVentureFundsData, setRotatingVentureFundsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRotatingVentureFunds = async () => {
      try {
        setLoading(true);
        const response = await fetchApi('/api/public/website-inputs/', {
          method: 'GET',
          params: {
            name: 'rotating_venture_funds'
          }
        });
        
        if (response.data && response.data.length > 0) {
          setRotatingVentureFundsData(response.data[0].input_json);
        }
      } catch (error) {
        console.error('Error fetching rotating venture funds data:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchRotatingVentureFunds();
  }, []);

  // Transform the data
  const { companyData, labelArrays } = transformRotatingVentureFundsData(rotatingVentureFundsData);

  if (loading) {
    return (
      <main>
        <div>
          <section className="content-section">
            <p>Co-Investors in LeapYear Teams</p>
            <div className="logo-loop">
              <div className="logo-track">
                <span className="label">Loading...</span>
              </div>
            </div>
          </section>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div>
          <section className="content-section">
            <p>Co-Investors in LeapYear Teams</p>
            <div className="logo-loop">
              <div className="logo-track">
                <span className="label">Error loading investor data</span>
              </div>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div>
        <section className="content-section">
          <p>Co-Investors in LeapYear Teams</p>
          {['first', 'second', 'third'].map((variant) => (
            <InvestorScroll
              key={variant}
              // ~7s per fund keeps every row at the pace the old 13-fund/90s rows had
              speed={`${Math.max(labelArrays[variant].length, 1) * 7}s`}
              variant={variant}
              companyData={companyData}
              labelArrays={labelArrays}
            />
          ))}
        </section>
      </div>
    </main>
  );
}

export default InvestorsRotating;
export { InvestorScroll };
